import {
  log,
  v2_createGooglePayMethodData,
  v2_isPaymentAwaitingPayment,
  v2_Order,
  v2_useGooglePayConfiguration,
} from '@crocodille/common';
import { Maybe } from '@crocodille/util';
import { useRouter } from '@framework/router';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { formatPrice } from './formatting';
import GooglePayButton from './google-pay-button/google-pay-button';

export function isGooglePaymentSupported(): boolean {
  return typeof window !== 'undefined' && 'PaymentRequest' in window;
}

export interface GooglePaymentProps {
  readonly order: v2_Order;
  readonly pending: boolean;
  readonly redirectUrl: Maybe<string>;
  pay(payload: {
    details: google.payments.api.PaymentData;
  }): Promise<PaymentComplete>;
}

const GooglePayment: React.FC<GooglePaymentProps> = ({
  order,
  pending,
  redirectUrl,
  pay,
}) => {
  const { t } = useTranslation();
  const { push } = useRouter();

  const { total, currencyCode } = order;

  const { data: configuration } = v2_useGooglePayConfiguration();

  const payment = order?.payment;

  const isAwaitingPayment = v2_isPaymentAwaitingPayment(payment);

  useEffect(() => {
    if (isAwaitingPayment && redirectUrl) {
      push(redirectUrl);
    }
  }, [isAwaitingPayment, redirectUrl, push]);

  const processPaymentRequest = useCallback(async () => {
    if (!configuration) {
      return;
    }

    const paymentRequest = new PaymentRequest(
      [
        {
          supportedMethods: 'https://google.com/pay',
          data: v2_createGooglePayMethodData(configuration),
        },
      ],
      {
        total: {
          label: t('checkout:payment.total'),
          amount: {
            currency: currencyCode,
            value: formatPrice(total),
          },
        },
      },
    );

    try {
      const response = await paymentRequest.show();

      if (response.details.errorCode) {
        log(
          'error',
          new Error(`[GooglePay] Error: ${response.details.statusCode}`),
          response.details,
        );
        response.complete('fail');

        return;
      }

      try {
        const details = response.details as google.payments.api.PaymentData;

        const result = await pay({
          details,
        });
        response.complete(result);
      } catch (e) {
        log('error', e as Error);
        response.complete('fail');
      }
    } catch (err) {
      log('error', err as Error);
    }
  }, [configuration, currencyCode, pay, t, total]);

  if (!configuration || !configuration.isEnabled) {
    return null;
  }

  return <GooglePayButton onClick={processPaymentRequest} loading={pending} />;
};

export default GooglePayment;
